import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const InteractiveNatureScene = ({ scene = 'forest', onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentElement, setCurrentElement] = useState(null);
  const [soundVolume, setSoundVolume] = useState([0.5]);
  const [sessionTime, setSessionTime] = useState(0);
  const [interactions, setInteractions] = useState(0);
  const [discoveries, setDiscoveries] = useState([]);
  const [showWisdom, setShowWisdom] = useState(false);
  const [currentWisdom, setCurrentWisdom] = useState('');

  const scenes = {
    forest: {
      name: "Enchanted Forest 🌲",
      background: "linear-gradient(135deg, #1e3a8a 0%, #065f46 50%, #166534 100%)",
      elements: [
        { id: 'tree1', name: '🌳', x: 20, y: 60, wisdom: "Trees teach us that growth takes time and patience." },
        { id: 'bird', name: '🐦', x: 70, y: 30, wisdom: "Birds remind us that freedom comes from letting go." },
        { id: 'flower', name: '🌸', x: 45, y: 75, wisdom: "Flowers bloom not for others, but because it's their nature." },
        { id: 'butterfly', name: '🦋', x: 60, y: 50, wisdom: "Transformation is beautiful, even when it's scary." },
        { id: 'mushroom', name: '🍄', x: 30, y: 80, wisdom: "Small things can have big impacts in the right environment." },
        { id: 'stream', name: '💧', x: 80, y: 70, wisdom: "Like water, you can flow around any obstacle." }
      ],
      sounds: 'Forest ambiance with birds and rustling leaves'
    },
    ocean: {
      name: "Peaceful Ocean 🌊",
      background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%)",
      elements: [
        { id: 'wave1', name: '🌊', x: 25, y: 70, wisdom: "Waves teach us that everything has its rhythm." },
        { id: 'seagull', name: '🕊️', x: 75, y: 25, wisdom: "Soar above your worries and see the bigger picture." },
        { id: 'shell', name: '🐚', x: 40, y: 85, wisdom: "Beautiful things wash up when you're patient and present." },
        { id: 'fish', name: '🐠', x: 60, y: 60, wisdom: "Sometimes you need to dive deep to find what you're looking for." },
        { id: 'starfish', name: '⭐', x: 15, y: 80, wisdom: "You have the power to regenerate and start fresh." },
        { id: 'lighthouse', name: '🗼', x: 85, y: 45, wisdom: "Be the light that guides others through their storms." }
      ],
      sounds: 'Ocean waves and seagull calls'
    },
    mountain: {
      name: "Majestic Mountains ⛰️",
      background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)",
      elements: [
        { id: 'peak', name: '⛰️', x: 50, y: 20, wisdom: "Reach for the peaks, but appreciate the journey." },
        { id: 'eagle', name: '🦅', x: 70, y: 35, wisdom: "Rise above challenges with grace and strength." },
        { id: 'cloud', name: '☁️', x: 30, y: 25, wisdom: "Clouds remind us that nothing stays the same forever." },
        { id: 'stone', name: '🪨', x: 40, y: 70, wisdom: "Be grounded like a stone, but flexible like the wind." },
        { id: 'flower2', name: '🌺', x: 65, y: 75, wisdom: "Beauty can bloom in the most challenging places." },
        { id: 'sun', name: '☀️', x: 80, y: 15, wisdom: "Your inner light can illuminate any darkness." }
      ],
      sounds: 'Mountain winds and distant bird calls'
    }
  };

  const currentScene = scenes[scene];

  const youthWisdomMessages = [
    "You're exactly where you need to be right now 🌟",
    "Your feelings are valid and temporary 💙",
    "You have unique gifts that the world needs ✨",
    "It's okay to not have everything figured out 🤗",
    "Small steps still move you forward 👣",
    "You're braver than you believe 🦁",
    "Your story is still being written 📖",
    "You belong here, just as you are 🏠"
  ];

  useEffect(() => {
    let timer;
    if (isActive) {
      timer = setInterval(() => {
        setSessionTime(prev => prev + 1);
        
        // Show random wisdom every 30 seconds
        if (sessionTime > 0 && sessionTime % 30 === 0) {
          const randomWisdom = youthWisdomMessages[Math.floor(Math.random() * youthWisdomMessages.length)];
          setCurrentWisdom(randomWisdom);
          setShowWisdom(true);
          setTimeout(() => setShowWisdom(false), 4000);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive, sessionTime]);

  const startSession = () => {
    setIsActive(true);
    setSessionTime(0);
    setInteractions(0);
    setDiscoveries([]);
  };

  const endSession = () => {
    setIsActive(false);
    if (onComplete) {
      onComplete({
        type: 'nature_scene',
        scene: scene,
        duration: sessionTime,
        interactions: interactions,
        discoveries: discoveries.length
      });
    }
  };

  const interactWithElement = (element) => {
    setCurrentElement(element);
    setInteractions(prev => prev + 1);
    
    if (!discoveries.includes(element.id)) {
      setDiscoveries(prev => [...prev, element.id]);
    }
    
    // Show element's wisdom
    setCurrentWisdom(element.wisdom);
    setShowWisdom(true);
    setTimeout(() => {
      setShowWisdom(false);
      setCurrentElement(null);
    }, 3000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="interactive-nature-scene">
      {/* Scene Header */}
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">{currentScene.name}</h2>
        <p className="text-gray-600">Click on elements in the scene to discover wisdom and insights</p>
        
        {isActive && (
          <div className="flex justify-center space-x-6 text-sm">
            <div className="glass-card px-4 py-2">
              ⏱️ {formatTime(sessionTime)}
            </div>
            <div className="glass-card px-4 py-2">
              👆 {interactions} interactions
            </div>
            <div className="glass-card px-4 py-2">
              🔍 {discoveries.length}/{currentScene.elements.length} discovered
            </div>
          </div>
        )}
      </div>

      {/* Interactive Scene */}
      <Card className="relative overflow-hidden" style={{ height: '400px' }}>
        {/* Background */}
        <div 
          className="absolute inset-0 transition-all duration-500"
          style={{ background: currentScene.background }}
        >
          {/* Animated background elements */}
          <div className="absolute inset-0">
            {/* Floating particles */}
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse opacity-30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </div>

        {/* Interactive Elements */}
        {currentScene.elements.map((element) => {
          const isDiscovered = discoveries.includes(element.id);
          const isActive = currentElement?.id === element.id;
          
          return (
            <button
              key={element.id}
              onClick={() => interactWithElement(element)}
              className={`absolute text-4xl transition-all duration-300 hover:scale-125 focus:scale-125 ${
                isDiscovered ? 'animate-bounce' : 'hover:animate-pulse'
              } ${isActive ? 'scale-150 animate-spin' : ''}`}
              style={{
                left: `${element.x}%`,
                top: `${element.y}%`,
                transform: 'translate(-50%, -50%)',
                filter: isDiscovered ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))' : 'none'
              }}
              title={`Click to discover wisdom from ${element.name}`}
            >
              {element.name}
            </button>
          );
        })}

        {/* Ripple effect for interactions */}
        {currentElement && (
          <div
            className="absolute w-16 h-16 border-4 border-white rounded-full animate-ping"
            style={{
              left: `${currentElement.x}%`,
              top: `${currentElement.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          />
        )}
      </Card>

      {/* Wisdom Display */}
      {showWisdom && (
        <Card className="glass-card p-6 bg-gradient-to-r from-purple-50 to-pink-50 animate-fade-in">
          <div className="text-center">
            <div className="text-2xl mb-3">✨</div>
            <p className="text-lg font-medium text-purple-800 italic">
              "{currentWisdom}"
            </p>
          </div>
        </Card>
      )}

      {/* Sound Controls */}
      <Card className="glass-card p-4">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-700 min-w-0 flex-shrink-0">
            🎵 {currentScene.sounds}
          </div>
          <div className="flex-1">
            <Slider
              value={soundVolume}
              onValueChange={setSoundVolume}
              max={1}
              min={0}
              step={0.1}
              className="w-full"
            />
          </div>
          <div className="text-sm text-gray-500 min-w-0 flex-shrink-0">
            {Math.round(soundVolume[0] * 100)}%
          </div>
        </div>
      </Card>

      {/* Controls */}
      <div className="text-center space-y-4">
        {!isActive ? (
          <div className="space-y-4">
            <Button onClick={startSession} className="btn-primary px-8 py-4 text-lg">
              🌱 Enter Nature Scene
            </Button>
            <p className="text-sm text-gray-600">
              Take your time exploring. There's no rush in nature.
            </p>
          </div>
        ) : (
          <div className="flex justify-center space-x-4">
            <Button onClick={endSession} className="btn-primary">
              ✅ Complete Session
            </Button>
            <Button 
              onClick={() => setIsActive(false)} 
              variant="outline" 
              className="btn-secondary"
            >
              ⏸️ Pause
            </Button>
          </div>
        )}
      </div>

      {/* Completion Stats */}
      {!isActive && sessionTime > 0 && (
        <Card className="glass-card p-6 bg-gradient-to-r from-blue-50 to-amber-50">
          <div className="text-center space-y-3">
            <h3 className="text-lg font-semibold text-emerald-800">Session Complete! 🎉</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-2xl text-blue-600">⏱️</div>
                <div className="font-medium">{formatTime(sessionTime)}</div>
                <div className="text-blue-700">Time Spent</div>
              </div>
              <div>
                <div className="text-2xl text-blue-600">👆</div>
                <div className="font-medium">{interactions}</div>
                <div className="text-blue-700">Interactions</div>
              </div>
              <div>
                <div className="text-2xl text-blue-600">🔍</div>
                <div className="font-medium">{discoveries.length}/{currentScene.elements.length}</div>
                <div className="text-blue-700">Discovered</div>
              </div>
            </div>
            <p className="text-blue-700 italic">
              "In nature, nothing exists alone. You are part of something beautiful." 🌿
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default InteractiveNatureScene;
